/**
 * SB6-ANA-DA-03 — Catalogue · Class Model readout, both halves editable
 * and INDEPENDENT axes.
 *
 * History:
 *   - R58-L-01 (lyse@5.9.0) shipped the readout with catalogue half as
 *     plain text and class-model half as a "lock-on-first-defect" picker.
 *   - SB6-ANA-DA-02 reversed the lock — class-model picker stayed
 *     interactive, gated by a confirm-modal once defects existed. But the
 *     BE still returned 409 class_model_locked, so the modal silently
 *     failed end-to-end against a real backend.
 *   - SB6-ANA-DA-03 first wave (lyse@7.3.0) made BOTH halves dropdowns
 *     and introduced `confirmReset: true` so the swap-with-reset is a
 *     real wire path.
 *   - SB6-ANA-DA-03 follow-up (lyse@7.4.0, this file's current state)
 *     DECOUPLES the class-model picker from the catalogue. Per
 *     Christian 2026-05-29: the catalogue's `class_model_id` is JUST a
 *     default; the analyst must be free to pick ANY class model for a
 *     DA — including the "None" model — regardless of the catalogue's
 *     default. The dropdown is fed by `GET /api/lyse/v1/class-models`
 *     (the flat global list) rather than derived from the catalogue.
 *
 * Render shape:
 *   - Catalogue half — dropdown over the tenant's catalogues.
 *   - Class-model half — dropdown over the FULL global class-models
 *     list. Always active. The legacy "type-only catalogue → disabled
 *     dropdown" branch is gone (the catalogue's missing default does
 *     NOT prevent the user from picking a class model for the DA).
 *
 * Lock policy (both halves):
 *   - Zero defects: pick → straight PATCH, no `confirmReset`.
 *   - ≥1 defect:    pick → confirm modal with a preview of doomed
 *                   defects → Continue sends `confirmReset: true`; BE
 *                   soft-deletes the listed defects in the same
 *                   transaction and appends the note
 *                   "soft deleted, because defect model was changed".
 *
 * Per Q2 from SB6-ANA-DA-02: AI findings' `class_uuid` is a static
 * parameter and is NOT touched by either swap.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@aeriosiq/api-client";
import {
  typography,
  compactFieldSx,
  compactMenuProps,
  compactControlHeight,
} from "@aeriosiq/ui";
import { useCataloguesStore } from "../../catalogues/cataloguesStore.js";
import { useClassModelsQuery } from "../../catalogues/useClassModelsQuery.js";
import { useDefectsQuery } from "../../defects/queries.js";
import { analysisKeys } from "../queries.js";

interface SetActiveClassModelResponse {
  dataAcquisitionId: string;
  activeClassModelId: string;
  classModelLockedAt: string | null;
  resetDefectCount?: number;
}

interface SetActiveCatalogueResponse {
  dataAcquisitionId: string;
  activeCatalogueId: string;
  classModelId: string;
  resetDefectCount?: number;
}

interface CatalogueOption {
  catalogueId: string;
  name: string;
}

interface ClassModelOption {
  classModelId: string;
  modelName: string;
  classCount: number;
}

export interface CatalogueReadoutProps {
  dataAcquisitionId: string;
  /** From `detail.activeCatalogueId` — null when no catalogue set yet. */
  initialActiveCatalogueId: string | null;
  /** From `detail.activeClassModelId` — null if inheriting from the catalogue. */
  initialActiveClassModelId: string | null;
  /** From `detail.classModelLockedAt` — non-null = lock had fired (pre-7.3.0
   *  legacy state); the swap-with-reset still works either way. */
  initialClassModelLockedAt: string | null;
  /**
   * Defect count for this DA. When omitted (undefined) the component
   * falls back to the existing `useDefectsQuery` hook for a belt-and-
   * braces lock check — same pattern the old ClassModelPicker used.
   *
   * The DataAcquisitionDetail DTO doesn't carry a `defectCount` field
   * yet; pass `undefined` to keep the fallback fetch active.
   */
  defectCount?: number | undefined;
}

const SEPARATOR = "·";
const PREVIEW_LIMIT = 5;
const SYNTHETIC_NO_CLASS_MODEL_IDS = new Set(["", "none"]);

type PendingChange =
  | { kind: "catalogue"; nextCatalogueId: string }
  | { kind: "class-model"; nextClassModelId: string };

export function CatalogueReadout({
  dataAcquisitionId,
  initialActiveCatalogueId,
  initialActiveClassModelId,
  initialClassModelLockedAt,
  defectCount,
}: CatalogueReadoutProps): React.ReactElement | null {
  const api = useApi();
  const queryClient = useQueryClient();
  void initialClassModelLockedAt;

  // ----- catalogue list ---------------------------------------------------
  const availableCatalogues = useCataloguesStore(
    (s) => s.availableCatalogues as typeof s.availableCatalogues | undefined,
  );
  const activeCatalogueIdFromStore = useCataloguesStore(
    (s) => s.activeCatalogueId,
  );
  const setActiveCatalogueIdInStore = useCataloguesStore(
    (s) => s.setActiveCatalogueId,
  );
  const catalogues = useMemo(
    () => availableCatalogues ?? [],
    [availableCatalogues],
  );

  // Local optimistic state — flips on PATCH success.
  const [activeCatalogueId, setActiveCatalogueId] = useState<string | null>(
    initialActiveCatalogueId,
  );
  const [activeClassModelId, setActiveClassModelId] = useState<string | null>(
    initialActiveClassModelId,
  );

  const resolvedCatalogueId =
    activeCatalogueId ?? activeCatalogueIdFromStore ?? initialActiveCatalogueId;

  const activeCatalogue = useMemo(() => {
    if (catalogues.length === 0) return null;
    if (resolvedCatalogueId) {
      const hit = catalogues.find((c) => c.catalogueId === resolvedCatalogueId);
      if (hit) return hit;
    }
    const def = catalogues.find((c) => c.isSystemDefault);
    return def ?? catalogues[0] ?? null;
  }, [catalogues, resolvedCatalogueId]);

  // ----- catalogue options (dropdown rows) -------------------------------
  const catalogueOptions = useMemo<CatalogueOption[]>(
    () =>
      catalogues.map((c) => ({
        catalogueId: c.catalogueId,
        name: c.name.en,
      })),
    [catalogues],
  );

  // ----- class-model options ---------------------------------------------
  // SB6-ANA-DA-03 (7.4.0) — the class-model picker is decoupled from the
  // catalogue. The dropdown is fed by the flat global class-models list,
  // independent of which catalogue is active. "None" (class_count = 0)
  // is a valid pick; sort by class_count ASC so it lands first.
  const classModelsQuery = useClassModelsQuery();
  const classModelOptions = useMemo<ClassModelOption[]>(() => {
    const items = classModelsQuery.data?.items ?? [];
    return items
      .map((m) => ({
        classModelId: m.classModelId,
        modelName: m.modelName.en,
        classCount: m.classCount,
      }))
      .sort((a, b) => a.classCount - b.classCount);
  }, [classModelsQuery.data]);

  // The catalogue's `class_model_id` is just a default — used to seed the
  // dropdown when the DA has no `active_class_model_id` set yet. Synthetic
  // "" / "none" sentinels map to null (no default).
  const catalogueClassModelId: string | null = (() => {
    const id = activeCatalogue?.classModel.classModelId ?? null;
    if (id === null) return null;
    if (SYNTHETIC_NO_CLASS_MODEL_IDS.has(id)) return null;
    return id;
  })();

  // ----- defect count fallback -------------------------------------------
  const defectsQuery = useDefectsQuery(dataAcquisitionId, {
    pageSize: PREVIEW_LIMIT,
  });
  const fallbackDefectsCount = defectsQuery.data?.total ?? 0;
  const effectiveDefectsCount = defectCount ?? fallbackDefectsCount;
  const previewDefects = defectsQuery.data?.items ?? [];

  // ----- pending-change state for the confirm modal ----------------------
  const [pending, setPending] = useState<PendingChange | null>(null);

  // ----- catalogue mutation ----------------------------------------------
  const setCatalogue = useMutation({
    mutationFn: async (vars: {
      catalogueId: string;
      confirmReset: boolean;
    }) => {
      const body: { catalogueId: string; confirmReset?: boolean } = {
        catalogueId: vars.catalogueId,
      };
      if (vars.confirmReset) body.confirmReset = true;
      const res = await api.patch<SetActiveCatalogueResponse>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/active-catalogue`,
        body,
      );
      return res.data;
    },
    onSuccess: (data) => {
      setActiveCatalogueId(data.activeCatalogueId);
      setActiveCatalogueIdInStore(data.activeCatalogueId);
      // The catalogue swap may have nudged the class-model too — clear
      // the local override so the next render derives it from the
      // (newly active) catalogue's classModel id.
      setActiveClassModelId(null);
      setPending(null);
      invalidateAfterSwap(queryClient, dataAcquisitionId);
    },
  });

  // ----- class-model mutation --------------------------------------------
  const setClassModel = useMutation({
    mutationFn: async (vars: {
      classModelId: string;
      confirmReset: boolean;
    }) => {
      const body: { classModelId: string; confirmReset?: boolean } = {
        classModelId: vars.classModelId,
      };
      if (vars.confirmReset) body.confirmReset = true;
      const res = await api.patch<SetActiveClassModelResponse>(
        `/data-acquisitions/${encodeURIComponent(dataAcquisitionId)}/active-class-model`,
        body,
      );
      return res.data;
    },
    onSuccess: (data) => {
      setActiveClassModelId(data.activeClassModelId);
      setPending(null);
      invalidateAfterSwap(queryClient, dataAcquisitionId);
    },
  });

  const isPending = setCatalogue.isPending || setClassModel.isPending;

  // ----- SB6-ANA-DEF-01 — auto-persist the displayed fallback ----------
  // The readout has always *displayed* a fallback catalogue
  // (`activeCatalogue` resolves to system_default → first when the DA has
  // no explicit pick) but never wrote that choice back to the DA. Result:
  // `createDefect` saw active_catalogue_id NULL and 422-rejected.
  // Christian (2026-05-31): "the fallback should be — to check what
  // catalogue is selected in the dropdown."
  // Fire `setCatalogue.mutate` on first mount when all three sources of
  // truth (local state, store, prop) are null and a fallback exists.
  // Guards mirror the existing handleCatalogueChange flow: defects-exist
  // → don't auto-swap (the user would otherwise lose a catalogue choice
  // they implicitly made by classifying defects).
  useEffect(() => {
    if (initialActiveCatalogueId != null) return;
    if (activeCatalogueId != null) return;
    if (activeCatalogueIdFromStore != null) return;
    if (catalogues.length === 0) return;
    if (effectiveDefectsCount > 0) return;
    if (setCatalogue.isPending) return;
    const fallback = activeCatalogue?.catalogueId;
    if (!fallback) return;
    setCatalogue.mutate({ catalogueId: fallback, confirmReset: false });
    // Run as soon as the conditions are met. After the mutation's
    // onSuccess sets `activeCatalogueId` locally + in the store, the
    // second / third guard short-circuits subsequent runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialActiveCatalogueId,
    activeCatalogueId,
    activeCatalogueIdFromStore,
    catalogues,
    effectiveDefectsCount,
    activeCatalogue?.catalogueId,
  ]);

  // ----- current values for the dropdowns --------------------------------
  const catalogueValue = activeCatalogue?.catalogueId ?? "";
  const classModelValue =
    activeClassModelId ??
    catalogueClassModelId ??
    classModelOptions[0]?.classModelId ??
    "";

  // ----- change handlers (route to modal when defects exist) -------------
  const handleCatalogueChange = useCallback(
    (next: string): void => {
      if (next === catalogueValue) return;
      if (effectiveDefectsCount > 0) {
        setPending({ kind: "catalogue", nextCatalogueId: next });
        return;
      }
      setCatalogue.mutate({ catalogueId: next, confirmReset: false });
    },
    [catalogueValue, effectiveDefectsCount, setCatalogue],
  );

  const handleClassModelChange = useCallback(
    (next: string): void => {
      if (next === classModelValue) return;
      if (effectiveDefectsCount > 0) {
        setPending({ kind: "class-model", nextClassModelId: next });
        return;
      }
      setClassModel.mutate({ classModelId: next, confirmReset: false });
    },
    [classModelValue, effectiveDefectsCount, setClassModel],
  );

  const handleConfirm = useCallback((): void => {
    if (pending === null) return;
    if (pending.kind === "catalogue") {
      setCatalogue.mutate({
        catalogueId: pending.nextCatalogueId,
        confirmReset: true,
      });
    } else {
      setClassModel.mutate({
        classModelId: pending.nextClassModelId,
        confirmReset: true,
      });
    }
  }, [pending, setCatalogue, setClassModel]);

  const handleCancel = useCallback((): void => {
    setPending(null);
  }, []);

  if (activeCatalogue === null) return null;

  // ----- render helpers --------------------------------------------------
  const separatorNode = (
    <Box
      component="span"
      aria-hidden
      sx={{
        fontSize: typography.size["body-md"],
        color: "text.primary",
        lineHeight: 1.2,
        px: 0.25,
      }}
    >
      {SEPARATOR}
    </Box>
  );

  const catalogueDropdown = (
    <TextField
      select
      size="small"
      hiddenLabel
      value={catalogueValue}
      disabled={isPending}
      onChange={(e) => {
        handleCatalogueChange(e.target.value);
      }}
      slotProps={{
        select: {
          // UIOPT-01 item 2 — dense portal menu (13px items, 32px rows).
          MenuProps: compactMenuProps,
          inputProps: { "aria-label": "Defect catalogue" },
        },
      }}
      sx={{
        minWidth: 180,
        // UIOPT-01 item 2 — consume the shared 13px compact-field preset so
        // the picker text (and thus its rendered height) shrinks; keep the
        // tight py override. Reuses `compactFieldSx`, no new magic numbers.
        ...compactFieldSx,
        // UIOPT-01 (2026-06-09) — pin the control to the shared dense height
        // so both dropdowns, the plain value labels and the CTA all sit on one
        // aligned second-row band.
        "& .MuiInputBase-root": { minHeight: compactControlHeight },
        "& .MuiInputBase-input": {
          ...compactFieldSx["& .MuiInputBase-input"],
          py: 0.5,
        },
      }}
      data-testid="catalogue-picker"
    >
      {catalogueOptions.map((opt) => (
        <MenuItem key={opt.catalogueId} value={opt.catalogueId}>
          {opt.name}
        </MenuItem>
      ))}
    </TextField>
  );

  const classModelDropdown = (
    <TextField
      select
      size="small"
      hiddenLabel
      value={classModelValue}
      disabled={isPending}
      onChange={(e) => {
        handleClassModelChange(e.target.value);
      }}
      slotProps={{
        input: {
          endAdornment: isPending ? (
            <CircularProgress size={14} sx={{ mr: 1 }} aria-hidden />
          ) : undefined,
        },
        select: {
          // UIOPT-01 item 2 — dense portal menu (13px items, 32px rows).
          MenuProps: compactMenuProps,
          inputProps: { "aria-label": "Class model" },
        },
      }}
      sx={{
        minWidth: 180,
        // UIOPT-01 item 2 — same shared 13px compact-field preset as the
        // catalogue half so both dropdowns shrink to one consistent height.
        ...compactFieldSx,
        // UIOPT-01 (2026-06-09) — pin the control to the shared dense height
        // so both dropdowns, the plain value labels and the CTA all sit on one
        // aligned second-row band.
        "& .MuiInputBase-root": { minHeight: compactControlHeight },
        "& .MuiInputBase-input": {
          ...compactFieldSx["& .MuiInputBase-input"],
          py: 0.5,
        },
      }}
      data-testid="class-model-picker"
      data-variant="editable"
    >
      {classModelOptions.map((opt) => (
        <MenuItem key={opt.classModelId} value={opt.classModelId}>
          {opt.modelName}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <>
      <FieldLabel label="Catalogue · Class Model">
        <Stack
          direction="row"
          spacing={0}
          alignItems="center"
          data-testid="catalogue-readout"
          data-variant="editable"
        >
          {catalogueDropdown}
          {separatorNode}
          {classModelDropdown}
        </Stack>
      </FieldLabel>

      <ConfirmResetDialog
        pending={pending}
        defectCount={effectiveDefectsCount}
        previewDefects={previewDefects}
        isPending={isPending}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  );
}

interface PreviewDefect {
  id: string;
  catalogueLabel: string;
  imageId: string;
}

function ConfirmResetDialog({
  pending,
  defectCount,
  previewDefects,
  isPending,
  onCancel,
  onConfirm,
}: {
  pending: PendingChange | null;
  defectCount: number;
  previewDefects: readonly PreviewDefect[];
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  const isCatalogueSwap = pending?.kind === "catalogue";
  const title = isCatalogueSwap
    ? "Change defect catalogue?"
    : "Change class model?";
  const countStr = String(defectCount);
  const previewCountStr = String(previewDefects.length);
  const noun = defectCount === 1 ? "defect" : "defects";
  const body = isCatalogueSwap
    ? `Changing the defect catalogue will delete ${countStr} ${noun} on this dataset. Continue?`
    : `Changing the class-model will reset ${countStr} ${noun}. Continue?`;
  const previewHeading =
    defectCount > previewDefects.length
      ? `Showing ${previewCountStr} of ${countStr} affected defect(s):`
      : `Affected defect(s):`;

  return (
    <Dialog
      open={pending !== null}
      onClose={onCancel}
      data-testid={
        isCatalogueSwap
          ? "catalogue-confirm-dialog"
          : "class-model-confirm-dialog"
      }
      aria-labelledby="catalogue-readout-confirm-title"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle id="catalogue-readout-confirm-title">{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>{body}</DialogContentText>
        {previewDefects.length > 0 ? (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="catalogue-readout-preview-heading"
            >
              {previewHeading}
            </Typography>
            <List
              dense
              data-testid="catalogue-readout-preview-list"
              sx={{
                maxHeight: 220,
                overflow: "auto",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                mt: 0.5,
              }}
            >
              {previewDefects.map((d) => (
                <ListItem key={d.id} disableGutters sx={{ px: 1 }}>
                  <ListItemText
                    primary={d.catalogueLabel}
                    secondary={`image ${d.imageId.slice(0, 8)}…`}
                  />
                </ListItem>
              ))}
            </List>
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onCancel}
          disabled={isPending}
          data-testid="catalogue-readout-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          disabled={isPending}
          variant="contained"
          color="warning"
          data-testid="catalogue-readout-continue"
        >
          {isPending ? <CircularProgress size={16} aria-hidden /> : "Continue"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Local Field-style label-on-top wrapper. Mirrors the inline `Field` in
 * `AnalysisHeader.tsx` so the readout sits flush with sibling header
 * fields without the components/ folder needing to import that file's
 * private helper.
 */
function FieldLabel({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.ReactElement {
  // UIOPT-01 — same deterministic label testid scheme as AnalysisHeader's
  // `Field` so AC1 can assert this label shares the header's top baseline.
  const slug = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
  return (
    <Stack spacing={0.25}>
      <Typography
        component="div"
        data-testid={`header-field-label-${slug}`}
        sx={{
          fontSize: typography.size["label-md"],
          letterSpacing: typography.tracking["label-md"],
          color: "text.secondary",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          fontSize: typography.size["body-md"],
          fontWeight: typography.weight.regular,
          color: "text.primary",
          lineHeight: 1.2,
          // UIOPT-01 (2026-06-09) — same dense-band height + vertical centring
          // as AnalysisHeader's `Field` value cell so the dropdowns line up
          // with the plain value labels in the second row.
          minHeight: compactControlHeight,
          display: "flex",
          alignItems: "center",
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}

/**
 * Drop every cached query that a catalogue or class-model swap can
 * invalidate. The DA detail carries the new `activeCatalogueId` /
 * `activeClassModelId` (consumed by AnalysisHeader, FilterWidget); the
 * data-points facets re-derive the L6 Severity options from the new
 * class model; defects + findings refetch in case the reset path ran.
 */
function invalidateAfterSwap(
  qc: ReturnType<typeof useQueryClient>,
  daId: string,
): void {
  void qc.invalidateQueries({ queryKey: analysisKeys.detail(daId) });
  void qc.invalidateQueries({ queryKey: analysisKeys.dataPoints(daId) });
  void qc.invalidateQueries({ queryKey: ["defects", daId] });
  void qc.invalidateQueries({ queryKey: ["findings", daId] });
}
